import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  message,
  Tooltip,
  Popconfirm,
  Modal,
  Dropdown,
} from 'antd';
import {
  CustomerServiceOutlined,
  ReloadOutlined,
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  DownOutlined,
  FileExcelOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import MainLayout from '../../layouts/MainLayout';
import { supportTicketsApi, supportTicketTypesApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';
import PersianDatePicker from '../../components/PersianDatePicker';
import { jalaliValueToApi } from '../../utils/jalaliApiDate';
import { formatApiDateTimeJalali } from '../../utils/dateUtils';
import { useListReturnState } from '../../hooks/useListReturnState';

const { Option } = Select;

const pickPaginator = (res) => {
  const body = res || {};
  return body.data && Array.isArray(body.data.data) ? body.data : body;
};

const DEFAULT_RANGE = () => {
  const end = moment();
  const start = moment().subtract(1, 'jMonth');
  const fmt = (m) => m.format('jYYYY-jMM-jDD').replace(/\//g, '-');
  return { startDate: fmt(start), endDate: fmt(end) };
};

const STATUS_META = {
  open: { text: 'باز', color: 'red' },
  pending: { text: 'در انتظار', color: 'gold' },
  resolved: { text: 'حل‌شده', color: 'green' },
  closed: { text: 'بسته‌شده', color: 'blue' },
};

const PRIORITY_META = {
  low: { text: 'پایین', color: 'default' },
  medium: { text: 'متوسط', color: 'blue' },
  high: { text: 'بالا', color: 'orange' },
  urgent: { text: 'فوری', color: 'red' },
};

const SupportTicketsSettings = () => {
  const navigate = useNavigate();
  const [{ startDate, endDate }, setRange] = useState(DEFAULT_RANGE);
  const [startVal, setStartVal] = useState(null);
  const [endVal, setEndVal] = useState(null);
  const [agentId, setAgentId] = useState('all');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [typeId, setTypeId] = useState('all');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filterOptions, setFilterOptions] = useState({ types: [], superadmins: [] });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeRows, setTypeRows] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');

  const captureCurrentListState = useCallback(
    () => ({
      startDate,
      endDate,
      startVal,
      endVal,
      agentId,
      status,
      priority,
      typeId,
      search,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [startDate, endDate, startVal, endVal, agentId, status, priority, typeId, search, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.startDate) setRange((prev) => ({ ...prev, startDate: restored.startDate }));
    if (restored.endDate) setRange((prev) => ({ ...prev, endDate: restored.endDate }));
    if (restored.startVal !== undefined) setStartVal(restored.startVal);
    if (restored.endVal !== undefined) setEndVal(restored.endVal);
    if (restored.agentId) setAgentId(restored.agentId);
    if (restored.status) setStatus(restored.status);
    if (restored.priority) setPriority(restored.priority);
    if (restored.typeId) setTypeId(restored.typeId);
    if (restored.search != null) setSearch(restored.search);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const buildListParams = useCallback(
    (page, perPage) => {
      const sd = jalaliValueToApi(startVal) || startDate;
      const ed = jalaliValueToApi(endVal) || endDate;
      return {
        startDate: sd,
        endDate: ed,
        agent_id: agentId,
        status,
        priority,
        type_id: typeId,
        search: search.trim() || undefined,
        page,
        per_page: perPage,
      };
    },
    [startDate, endDate, startVal, endVal, agentId, status, priority, typeId, search],
  );

  const fetchList = useCallback(
    async (page = 1, perPage = 10) => {
      setLoading(true);
      try {
        const params = buildListParams(page, perPage);
        const res = await supportTicketsApi.list(params);
        const pag = pickPaginator(res);
        setRows(pag.data || []);
        setPagination({
          current: Number(pag.current_page || page),
          pageSize: Number(pag.per_page || perPage),
          total: Number(pag.total || 0),
        });
        const f = res?.filters || {};
        setFilterOptions({
          types: f.types || [],
          superadmins: f.superadmins || [],
        });
        const sd = jalaliValueToApi(startVal) || startDate;
        const ed = jalaliValueToApi(endVal) || endDate;
        setRange({ startDate: sd, endDate: ed });
      } catch (error) {
        message.error(error?.message || 'خطا در دریافت تیکت‌ها');
      } finally {
        setLoading(false);
      }
    },
    [buildListParams, startDate, endDate, startVal, endVal],
  );

  useEffect(() => {
    fetchList(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startVal, endVal, agentId, status, priority, typeId, search]);

  const onDelete = useCallback(
    async (id) => {
      try {
        await supportTicketsApi.delete(id);
        message.success('تیکت حذف شد');
        fetchList(pagination.current, pagination.pageSize);
      } catch (error) {
        message.error(error?.message || 'خطا در حذف');
      }
    },
    [fetchList, pagination.current, pagination.pageSize],
  );

  const handleBulkDelete = useCallback(() => {
    const keys = [...selectedRowKeys];
    if (!keys.length) return;
    Modal.confirm({
      className: 'custom-font',
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف تیکت‌های انتخاب‌شده</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف {keys.length} مورد اطمینان دارید؟</span>
      ),
      okText: 'حذف',
      cancelText: 'لغو',
      okButtonProps: {
        danger: true,
        style: { fontFamily: 'CustomFont', backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' },
      },
      cancelButtonProps: { style: { fontFamily: 'CustomFont' } },
      onOk: async () => {
        setBulkLoading(true);
        try {
          await supportTicketsApi.applyQuickAction('delete', keys);
          message.success('حذف انجام شد');
          setSelectedRowKeys([]);
          fetchList(pagination.current, pagination.pageSize);
        } catch (error) {
          message.error(error?.message || 'خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  }, [selectedRowKeys, fetchList, pagination.current, pagination.pageSize]);

  const handleBulkStatus = useCallback(
    (newStatus) => {
      const keys = [...selectedRowKeys];
      if (!keys.length) return;
      const label = STATUS_META[newStatus]?.text || newStatus;
      Modal.confirm({
        className: 'custom-font',
        title: <span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت</span>,
        content: (
          <span style={{ fontFamily: 'CustomFont' }}>
            وضعیت {keys.length} تیکت به «{label}» تغییر کند؟
          </span>
        ),
        okText: 'بله',
        cancelText: 'لغو',
        okButtonProps: { className: PANEL_PRIMARY_BTN_CLASS, style: { fontFamily: 'CustomFont' } },
        cancelButtonProps: { style: { fontFamily: 'CustomFont' } },
        onOk: async () => {
          setBulkLoading(true);
          try {
            await supportTicketsApi.applyQuickAction('change-status', keys, newStatus);
            message.success('وضعیت بروزرسانی شد');
            setSelectedRowKeys([]);
            fetchList(pagination.current, pagination.pageSize);
          } catch (error) {
            message.error(error?.message || 'خطا در تغییر وضعیت');
          } finally {
            setBulkLoading(false);
          }
        },
      });
    },
    [selectedRowKeys, fetchList, pagination.current, pagination.pageSize],
  );

  const bulkMenuItems = useMemo(
    () => [
      {
        key: 'del',
        label: 'حذف انتخاب‌شده‌ها',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: handleBulkDelete,
      },
      { type: 'divider' },
      ...Object.keys(STATUS_META).map((key) => ({
        key: `st-${key}`,
        label: `وضعیت: ${STATUS_META[key].text}`,
        onClick: () => handleBulkStatus(key),
      })),
    ],
    [handleBulkDelete, handleBulkStatus],
  );

  const onExport = async () => {
    setExportLoading(true);
    try {
      const params = buildListParams(1, pagination.pageSize);
      await supportTicketsApi.exportList(params);
      message.success('خروجی دریافت شد');
    } catch (error) {
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExportLoading(false);
    }
  };

  const openTypesModal = async () => {
    setTypesModalOpen(true);
    setEditingType(null);
    setNewTypeName('');
    setEditTypeName('');
    setTypesLoading(true);
    try {
      const res = await supportTicketTypesApi.getCreateMeta();
      const d = res?.data || res;
      setTypeRows(d.ticket_types || []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت انواع');
      setTypeRows([]);
    } finally {
      setTypesLoading(false);
    }
  };

  const addType = async () => {
    const name = newTypeName.trim();
    if (!name) return;
    setTypesLoading(true);
    try {
      await supportTicketTypesApi.create({ type: name });
      message.success('ثبت شد');
      setNewTypeName('');
      const res = await supportTicketTypesApi.getCreateMeta();
      const d = res?.data || res;
      setTypeRows(d.ticket_types || []);
      fetchList(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error);
      else message.error(error?.message || 'خطا');
    } finally {
      setTypesLoading(false);
    }
  };

  const saveType = async () => {
    if (!editingType?.id) return;
    const name = editTypeName.trim();
    if (!name) return;
    setTypesLoading(true);
    try {
      await supportTicketTypesApi.update(editingType.id, { type: name });
      message.success('بروزرسانی شد');
      setEditingType(null);
      const res = await supportTicketTypesApi.getCreateMeta();
      const d = res?.data || res;
      setTypeRows(d.ticket_types || []);
      fetchList(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error);
      else message.error(error?.message || 'خطا');
    } finally {
      setTypesLoading(false);
    }
  };

  const removeType = async (id) => {
    setTypesLoading(true);
    try {
      await supportTicketTypesApi.delete(id);
      message.success('حذف شد');
      const res = await supportTicketTypesApi.getCreateMeta();
      const d = res?.data || res;
      setTypeRows(d.ticket_types || []);
      fetchList(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    } finally {
      setTypesLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'موضوع',
        dataIndex: 'subject',
        key: 'subject',
        ellipsis: true,
        render: (t) => <span style={{ fontFamily: 'CustomFont', fontWeight: 600 }}>{t || '—'}</span>,
      },
      {
        title: 'شرکت',
        key: 'company',
        ellipsis: true,
        responsive: ['md'],
        render: (_, r) => (
          <span className="truncate block" style={{ fontFamily: 'CustomFont' }}>
            {r.company?.company_name || '—'}
          </span>
        ),
      },
      {
        title: 'درخواست‌دهنده',
        key: 'requester',
        width: 140,
        ellipsis: true,
        render: (_, r) => <span style={{ fontFamily: 'CustomFont' }}>{r.requester?.name || '—'}</span>,
      },
      {
        title: 'پشتیبان',
        key: 'agent',
        width: 130,
        ellipsis: true,
        render: (_, r) => <span style={{ fontFamily: 'CustomFont' }}>{r.agent?.name || '—'}</span>,
      },
      {
        title: 'وضعیت',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (s) => {
          const m = STATUS_META[s] || { text: s, color: 'default' };
          return <Tag color={m.color}>{m.text}</Tag>;
        },
      },
      {
        title: 'اولویت',
        dataIndex: 'priority',
        key: 'priority',
        width: 100,
        render: (p) => {
          const m = PRIORITY_META[p] || { text: p, color: 'default' };
          return <Tag color={m.color}>{m.text}</Tag>;
        },
      },
      {
        title: 'تاریخ',
        dataIndex: 'created_on',
        key: 'created_on',
        width: 160,
        responsive: ['lg'],
        ellipsis: true,
        render: (t) => formatApiDateTimeJalali(t) || '—',
      },
      {
        title: 'عملیات',
        key: 'actions',
        width: 140,
        align: 'center',
        fixed: 'right',
        render: (_, r) => (
          <div className="flex w-full justify-center">
            <Space size="small">
              <Tooltip title="مشاهده">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => navigateToDetail(`/settings/support-tickets/${r.id}`)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                />
              </Tooltip>
              <Popconfirm title="حذف این تیکت؟" okText="حذف" cancelText="لغو" onConfirm={() => onDelete(r.id)}>
                <Tooltip title="حذف">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          </div>
        ),
      },
    ],
    [navigateToDetail, onDelete],
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-900/25 flex-shrink-0">
                <CustomerServiceOutlined className="text-lg sm:text-xl text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">تیکت پشتیبانی</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm m-0 truncate">تیکت‌های شرکت‌ها بین سوپرادمین‌ها</p> */}
              </div>
            </div>
            <Space wrap>
              <Button icon={<TagsOutlined />} onClick={openTypesModal}>
                انواع تیکت
              </Button>
              <Button icon={<FileExcelOutlined />} loading={exportLoading} onClick={onExport}>
                خروجی اکسل
              </Button>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchList(pagination.current, pagination.pageSize)}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={() => navigate('/settings/support-tickets/create')}>
                تیکت جدید
              </Button>
            </Space>
          </div>

          <div className="bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50 rounded-b-xl">
            {selectedRowKeys.length > 0 ? (
              <div className={`${BULK_ACTION_BAR_CLASS} flex-wrap`}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkMenuItems }} trigger={['click']} disabled={bulkLoading}>
                  <Button
                    type="primary"
                    loading={bulkLoading}
                    size="middle"
                    className="border-0"
                    icon={<DownOutlined />}
                    style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
              </div>
            ) : null}

            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجو..."
                prefix={<SearchOutlined className="text-gray-400" />}
                className="rounded-lg xl:col-span-2"
                style={{ fontFamily: 'CustomFont', width: '100%' }}
              />
              <div className="grid grid-cols-2 gap-2 lg:col-span-2">
                <PersianDatePicker value={startVal} onChange={setStartVal} placeholder="از تاریخ" />
                <PersianDatePicker value={endVal} onChange={setEndVal} placeholder="تا تاریخ" />
              </div>
              <Select value={agentId} onChange={setAgentId} popupClassName="custom-font" style={{ width: '100%', fontFamily: 'CustomFont' }}>
                <Option value="all">همه پشتیبان‌ها</Option>
                {(filterOptions.superadmins || []).map((a) => (
                  <Option key={a.id} value={String(a.id)}>
                    {a.name}
                  </Option>
                ))}
              </Select>
              <Select value={status} onChange={setStatus} popupClassName="custom-font" style={{ width: '100%', fontFamily: 'CustomFont' }}>
                <Option value="all">همه وضعیت‌ها</Option>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <Option key={k} value={k}>
                    {v.text}
                  </Option>
                ))}
              </Select>
              <Select value={priority} onChange={setPriority} popupClassName="custom-font" style={{ width: '100%', fontFamily: 'CustomFont' }}>
                <Option value="all">همه اولویت‌ها</Option>
                {Object.entries(PRIORITY_META).map(([k, v]) => (
                  <Option key={k} value={k}>
                    {v.text}
                  </Option>
                ))}
              </Select>
              <Select value={typeId} onChange={setTypeId} popupClassName="custom-font" style={{ width: '100%', fontFamily: 'CustomFont' }}>
                <Option value="all">همه انواع</Option>
                {(filterOptions.types || []).map((t) => (
                  <Option key={t.id} value={String(t.id)}>
                    {t.type}
                  </Option>
                ))}
              </Select>
            </div>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              <Table
                rowKey="id"
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={rows}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  responsive: true,
                  onChange: (page, pageSize) => fetchList(page, pageSize),
                }}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          </div>
        </Card>
      </div>

      <Modal
        wrapClassName="custom-font"
        classNames={{ body: 'custom-font' }}
        title={
          <div className="flex items-center gap-3 py-1">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-900/25">
              <TagsOutlined className="text-lg text-sky-600 dark:text-sky-400" />
            </span>
            <div className="min-w-0 text-right">
              <div className="text-base font-bold text-gray-800 dark:text-gray-100">انواع تیکت پشتیبانی</div>
              <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">مدیریت نوع تیکت برای فیلتر و ایجاد</div>
            </div>
          </div>
        }
        open={typesModalOpen}
        onCancel={() => {
          setTypesModalOpen(false);
          setEditingType(null);
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div className="mb-3 flex flex-wrap gap-2 items-end" style={{ fontFamily: 'CustomFont' }}>
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">نوع جدید</div>
            <Input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="نام نوع"
              style={{ fontFamily: 'CustomFont' }}
            />
          </div>
          <Button type="primary" loading={typesLoading} onClick={addType} className={PANEL_PRIMARY_BTN_CLASS}>
            افزودن
          </Button>
        </div>
        <Table
          size="small"
          rowKey="id"
          loading={typesLoading}
          dataSource={typeRows}
          pagination={false}
          className="custom-font"
          columns={[
            {
              title: 'نوع',
              dataIndex: 'type',
              key: 'type',
              render: (t, r) =>
                editingType?.id === r.id ? (
                  <Input
                    size="small"
                    value={editTypeName}
                    onChange={(e) => setEditTypeName(e.target.value)}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                ) : (
                  <span style={{ fontFamily: 'CustomFont' }}>{t}</span>
                ),
            },
            {
              title: 'عملیات',
              key: 'act',
              width: 160,
              render: (_, r) =>
                editingType?.id === r.id ? (
                  <Space>
                    <Button size="small" type="primary" loading={typesLoading} onClick={saveType} className={PANEL_PRIMARY_BTN_CLASS}>
                      ذخیره
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingType(null);
                        setEditTypeName('');
                      }}
                    >
                      لغو
                    </Button>
                  </Space>
                ) : (
                  <Space>
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      onClick={() => {
                        setEditingType(r);
                        setEditTypeName(r.type || '');
                      }}
                      className="border-green-600 text-green-700"
                    >
                      ویرایش
                    </Button>
                    <Popconfirm
                      title="حذف این نوع؟"
                      okText="حذف"
                      cancelText="لغو"
                      okButtonProps={{ danger: true, style: { fontFamily: 'CustomFont' } }}
                      cancelButtonProps={{ style: { fontFamily: 'CustomFont' } }}
                      onConfirm={() => removeType(r.id)}
                    >
                      <Button size="small" danger>
                        حذف
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
            },
          ]}
        />
      </Modal>
    </MainLayout>
  );
};

export default SupportTicketsSettings;
